import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { get, map, some, sortBy, unionBy } from 'lodash/fp';
import styled from 'styled-components';
import {
  Checkbox,
  CheckboxGroup,
  ControlLabel,
  FormControl,
  HelpBlock,
  Icon,
  Loader
} from 'rsuite';
import { fetchReservationTrackingMethodOptions } from '../../../redux/Reservation/sagas/routines';
import {
  getIsFetching,
  getOrderedOptions as getTrackingMethodOptions
} from '../../../redux/Reservation/selectors/tracking-method-options';
import { CompressFormGroup } from '../../widgets/FormElements';
import { OptionType } from '../../../interfaces';

const ProgramsCheckboxGroup = styled(CheckboxGroup)`
  max-width: 766px;
  margin-left: 0;
  border-radius: 6px;
  padding-top: 5px;
  padding-bottom: 5px;
  border: 1px solid #e5e5ea;
`;

const ProgramCheckBox = styled(Checkbox)`
  width: 200px;
  vertical-align: top;
  .rs-checkbox-checker {
    padding-bottom: 0;
  }
  .rs-checkbox-checker > label {
    font-size: 12px;
    white-space: initial;
    text-transform: none;
  }
  &.ineligible > .rs-checkbox-checker > label {
    color: #f44336;
    text-decoration: line-through;
  }
`;

const ProgramsHelpBlock = styled(HelpBlock)`
  margin-left: 192px;
  margin-bottom: 5px;
  padding-top: 5px;
  color: #f44336;
`;

const ProgramsSelection = ({
  name,
  defaultValue,
  disabled,
  hasError,
  guestId,
  additionalOptions
}) => {
  const dispatch = useDispatch();
  const isFetching = useSelector(getIsFetching);
  const trackingMethodOptions = useSelector(getTrackingMethodOptions);

  useEffect(
    () => {
      dispatch(fetchReservationTrackingMethodOptions(guestId));
    },
    [guestId]
  );

  const programOptions = useMemo(
    () => {
      if (additionalOptions.length) {
        return sortBy(
          'label',
          unionBy('value', additionalOptions, trackingMethodOptions)
        );
      }

      return trackingMethodOptions;
    },
    [additionalOptions, trackingMethodOptions]
  );

  const hasIneligibleProgram = useMemo(
    () =>
      some(
        ({ meta }) => get('isGuestEligible', meta) === false,
        programOptions
      ),
    [programOptions]
  );

  return (
    <CompressFormGroup className={hasError && 'error'}>
      {hasIneligibleProgram && (
        <ProgramsHelpBlock>
          <Icon icon="info" />
          &nbsp;Some programs may appear crossed out if the Guest didn&#39;t
          meet the program&#39;s guest conditions.
        </ProgramsHelpBlock>
      )}
      <ControlLabel style={{ fontSize: 12 }}>
        Select Help Desired * <br />
      </ControlLabel>
      {isFetching ? (
        <Loader style={{ padding: 10 }} />
      ) : (
        <FormControl
          inline
          name={name}
          errorMessage=""
          defaultValue={defaultValue}
          accepter={ProgramsCheckboxGroup}
        >
          {map(({ label, value, meta = {} }) => {
            const { isGuestEligible = true } = meta;
            return (
              <ProgramCheckBox
                key={value}
                value={value}
                disabled={disabled || !isGuestEligible}
                className={!isGuestEligible ? 'ineligible' : ''}
              >
                {label}
              </ProgramCheckBox>
            );
          }, programOptions)}
        </FormControl>
      )}
    </CompressFormGroup>
  );
};

ProgramsSelection.propTypes = {
  name: PropTypes.string,
  defaultValue: PropTypes.arrayOf(PropTypes.number),
  hasError: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  guestId: PropTypes.number,
  additionalOptions: PropTypes.arrayOf(OptionType)
};

ProgramsSelection.defaultProps = {
  name: 'programs',
  defaultValue: [],
  disabled: false,
  guestId: null,
  additionalOptions: []
};

export default ProgramsSelection;
